import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Table,
  Badge,
  Modal,
  Form,
  ListGroup,
  InputGroup,
  Tab,
  Nav,
  Alert,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaEdit,
  FaTrash,
  FaUsers,
  FaBook,
  FaMoneyBill,
  FaClock,
  FaUserPlus,
  FaSearch,
  FaSave,
  FaTimes,
  FaFilePdf,
  FaDownload,
  FaChevronRight,
  FaChevronDown,
  FaStar,
  FaCertificate,
  FaRupeeSign,
  FaInfoCircle,
  FaImage,
  FaCalendarAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import CourseService from "../../../services/courseService";
import StudentService from "../../../services/studentService";
import Header from "../../../components/common/Header";
import CourseForm from "./CourseForm";

const CourseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Course state
  const [course, setCourse] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // Accordion state for modules
  const [activeModules, setActiveModules] = useState([]);

  // Edit course modal state
  const [showEditModal, setShowEditModal] = useState(false);

  // Add student modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMethod, setAddMethod] = useState("manual");
  const [searchTerm, setSearchTerm] = useState("");
  const [availableStudents, setAvailableStudents] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  
  // NEW: Custom fee state
  const [customFee, setCustomFee] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState(null);

  // Manual add form state
  const [manualStudent, setManualStudent] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [manualErrors, setManualErrors] = useState({});

  useEffect(() => {
    loadCourseDetails();
    loadEnrolledStudents();
  }, [id]);

  const loadCourseDetails = async () => {
    try {
      setLoading(true);
      const response = await CourseService.getCourseById(id);
      let courseData = response.data;
      if (response.data && response.data.data) {
        courseData = response.data.data;
      }
      setCourse(courseData);
      
      // Set default custom fee to course price
      if (courseData?.price) {
        setCustomFee(courseData.price.toString());
      }

      // Parse modules from long_description
      if (courseData?.long_description) {
        parseModules(courseData.long_description);
      }
    } catch (error) {
      toast.error("Failed to load course details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const parseModules = (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const h3Elements = doc.querySelectorAll("h3");

    const modules = [];
    h3Elements.forEach((h3) => {
      let content = "";
      let nextElement = h3.nextElementSibling;
      while (nextElement && nextElement.tagName !== "H3") {
        if (
          nextElement.tagName === "P" ||
          nextElement.tagName === "UL" ||
          nextElement.tagName === "OL"
        ) {
          content += nextElement.outerHTML;
        }
        nextElement = nextElement.nextElementSibling;
      }
      modules.push({
        title: h3.textContent,
        content: content,
      });
    });

    setActiveModules(modules.map((_, index) => ({ index, isOpen: false })));
    return modules;
  };

  const getModules = () => {
    if (!course?.long_description) return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(course.long_description, "text/html");
    const h3Elements = doc.querySelectorAll("h3");

    const modules = [];
    h3Elements.forEach((h3) => {
      let content = "";
      let nextElement = h3.nextElementSibling;
      while (nextElement && nextElement.tagName !== "H3") {
        if (
          nextElement.tagName === "P" ||
          nextElement.tagName === "UL" ||
          nextElement.tagName === "OL"
        ) {
          content += nextElement.outerHTML;
        }
        nextElement = nextElement.nextElementSibling;
      }
      modules.push({
        title: h3.textContent,
        content: content,
      });
    });
    return modules;
  };

  const toggleModule = (index) => {
    setActiveModules((prev) =>
      prev.map((item) =>
        item.index === index ? { ...item, isOpen: !item.isOpen } : item,
      ),
    );
  };

  const loadEnrolledStudents = async () => {
    try {
      setLoadingStudents(true);
      const response = await CourseService.getEnrolledStudents(id);
      setStudents(response.data || []);
    } catch (error) {
      toast.error("Failed to load enrolled students");
      console.error(error);
    } finally {
      setLoadingStudents(false);
    }
  };

  const loadAvailableStudents = async () => {
    try {
      setLoadingAvailable(true);
      const response = await CourseService.getAvailableStudentsForCourse(id);
      const studentsData = Array.isArray(response.data) ? response.data : [];
      setAvailableStudents(studentsData);
    } catch (error) {
      console.error('Failed to load available students:', error);
      toast.error('Failed to load student list');
      setAvailableStudents([]);
    } finally {
      setLoadingAvailable(false);
    }
  };

  const handleDownloadSyllabus = async () => {
    try {
      await CourseService.downloadSyllabus(id);
      toast.success('Syllabus download started');
    } catch (error) {
      toast.error('Failed to download syllabus');
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this course?")) {
      try {
        await CourseService.deleteCourse(id);
        toast.success("Course deleted successfully");
        navigate("/admin/courses");
      } catch (error) {
        toast.error("Failed to delete course");
      }
    }
  };

  const validateManualForm = () => {
    const errors = {};
    if (!manualStudent.name.trim()) errors.name = "Name is required";
    if (!manualStudent.email.trim()) errors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(manualStudent.email))
      errors.email = "Email is invalid";
    if (!manualStudent.phone.trim()) errors.phone = "Phone is required";

    setManualErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // UPDATED: Send custom_fee with manual add
  const handleManualAdd = async () => {
    if (!validateManualForm()) return;
    
    // Validate custom fee
    const feeValue = parseFloat(customFee);
    if (isNaN(feeValue) || feeValue < 0) {
      toast.error("Please enter a valid course fee");
      return;
    }

    try {
      const studentData = {
        ...manualStudent,
        custom_fee: feeValue
      };
      const response = await CourseService.addStudentManuallyToCourse(
        id,
        studentData,
      );
      toast.success(
        `Student added successfully! Password: ${response.data.password}`,
      );
      setShowAddModal(false);
      setManualStudent({ name: "", email: "", phone: "" });
      setManualErrors({});
      setCustomFee(course?.price?.toString() || "0");
      loadEnrolledStudents();
      loadCourseDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add student");
    }
  };

  // UPDATED: Send custom_fee with existing student
  const handleAddExisting = async (studentId) => {
    // Validate custom fee
    const feeValue = parseFloat(customFee);
    if (isNaN(feeValue) || feeValue < 0) {
      toast.error("Please enter a valid course fee");
      return;
    }

    try {
      await CourseService.addExistingStudentToCourse(id, studentId, feeValue);
      toast.success("Student added to course successfully");
      setShowAddModal(false);
      setSelectedStudentId(null);
      setCustomFee(course?.price?.toString() || "0");
      loadEnrolledStudents();
      loadCourseDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add student");
    }
  };

  const openAddModal = () => {
    setShowAddModal(true);
    setAddMethod("manual");
    setManualStudent({ name: "", email: "", phone: "" });
    setManualErrors({});
    setSearchTerm("");
    setCustomFee(course?.price?.toString() || "0");
    setSelectedStudentId(null);
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    loadCourseDetails();
    toast.success("Course updated successfully");
  };

  const modules = getModules();

  if (loading) {
    return (
      <div>
        <Header />
        <div className="container-fluid p-4 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div>
        <Header />
        <div className="container-fluid p-4 text-center">
          <h3>Course not found</h3>
          <Button variant="primary" onClick={() => navigate("/admin/courses")}>
            Back to Courses
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="container-fluid p-4">
        {/* Back Button */}
        <Button
          variant="link"
          className="mb-3 text-decoration-none"
          onClick={() => navigate("/admin/courses")}
        >
          <FaArrowLeft className="me-2" /> Back to Courses
        </Button>

        <Row>
          <Col md={8}>
            {/* Course Header Card */}
            <Card className="mb-4 shadow-sm border-0 overflow-hidden">
              {course.image_url && (
                <div 
                  className="position-relative" 
                  style={{ height: "200px", background: "#f0f2f5" }}
                >
                  <img
                    src={course.image_url}
                    alt={course.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <div className="position-absolute top-0 start-0 m-3">
                    <Badge bg="primary" className="px-3 py-2">
                      <FaStar className="me-1" /> Featured Course
                    </Badge>
                  </div>
                </div>
              )}
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h2 className="mb-2">{course.name}</h2>
                    <p className="text-muted mb-0">{course.description}</p>
                  </div>
                  <div>
                    <Button
                      variant="warning"
                      size="sm"
                      className="me-2"
                      onClick={() => setShowEditModal(true)}
                    >
                      <FaEdit className="me-1" /> Edit
                    </Button>
                    <Button variant="danger" size="sm" onClick={handleDelete}>
                      <FaTrash className="me-1" /> Delete
                    </Button>
                  </div>
                </div>

                <Row className="mt-4">
                  <Col md={4}>
                    <div className="d-flex align-items-center p-3 bg-light rounded">
                      <FaRupeeSign className="text-success fs-4 me-3" />
                      <div>
                        <small className="text-muted d-block">Price</small>
                        <strong className="fs-5">
                          ₹{parseFloat(course.price).toLocaleString()}
                        </strong>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="d-flex align-items-center p-3 bg-light rounded">
                      <FaClock className="text-info fs-4 me-3" />
                      <div>
                        <small className="text-muted d-block">Duration</small>
                        <strong className="fs-5">{course.duration}</strong>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="d-flex align-items-center p-3 bg-light rounded">
                      <FaUsers className="text-primary fs-4 me-3" />
                      <div>
                        <small className="text-muted d-block">Enrolled</small>
                        <strong className="fs-5">{students.length} Students</strong>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Syllabus Section */}
                {course.syllabus_name && (
                  <div className="mt-3 p-3 bg-light rounded d-flex justify-content-between align-items-center">
                    <div>
                      <FaFilePdf className="text-danger me-2 fs-5" />
                      <strong>Syllabus:</strong> {course.syllabus_name}
                    </div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleDownloadSyllabus}
                    >
                      <FaDownload className="me-1" /> Download
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Course Curriculum Section */}
            <Card className="mb-4 shadow-sm">
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">
                  <FaBook className="me-2" /> Course Curriculum
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-4">
                  <h6 className="text-primary mb-3">
                    <FaInfoCircle className="me-2" /> Course Overview
                  </h6>
                  <p>{course.description}</p>
                </div>

                <div>
                  <h6 className="text-primary mb-3">
                    <FaBook className="me-2" /> What You'll Learn
                  </h6>

                  {modules.length > 0 ? (
                    <div className="modules-accordion">
                      {modules.map((module, index) => {
                        const isOpen =
                          activeModules.find((m) => m.index === index)?.isOpen || false;
                        return (
                          <div key={index} className="module-item" style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            marginBottom: "12px",
                            overflow: "hidden"
                          }}>
                            <div
                              className="module-header"
                              onClick={() => toggleModule(index)}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "15px 20px",
                                backgroundColor: isOpen ? "#e7f1ff" : "#f8f9fa",
                                cursor: "pointer",
                                transition: "all 0.3s ease"
                              }}
                            >
                              <div className="module-header-left" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                {isOpen ? (
                                  <FaChevronDown className="text-primary" />
                                ) : (
                                  <FaChevronRight className="text-primary" />
                                )}
                                <Badge bg="secondary" style={{ borderRadius: "20px" }}>
                                  Module {index + 1}
                                </Badge>
                                <h6 className="mb-0" style={{ fontWeight: "600" }}>{module.title}</h6>
                              </div>
                            </div>
                            {isOpen && (
                              <div className="module-content" style={{
                                padding: "20px",
                                backgroundColor: "white",
                                borderTop: "1px solid #e0e0e0"
                              }}>
                                <div
                                  dangerouslySetInnerHTML={{ __html: module.content }}
                                  style={{ lineHeight: "1.6" }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted">
                      <FaBook size={40} className="mb-3 opacity-50" />
                      <p>No curriculum content available.</p>
                      <small>Edit the course and add content in the "Long Description" field using ### headings for modules.</small>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-3 border-top">
                  <h6 className="text-primary mb-3">
                    <FaStar className="me-2" /> Key Features
                  </h6>
                  <Row className="g-3">
                    <Col md={4}>
                      <div className="d-flex align-items-center">
                        <FaCertificate className="text-success me-2" />
                        <small>Industry Certificate</small>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex align-items-center">
                        <FaUsers className="text-success me-2" />
                        <small>Expert Instructors</small>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex align-items-center">
                        <FaBook className="text-success me-2" />
                        <small>Hands-on Projects</small>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card.Body>
            </Card>

            {/* Enrolled Students Table */}
            <Card className="shadow-sm">
              <Card.Header className="bg-info text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <FaUsers className="me-2" /> Enrolled Students
                </h5>
                <Button
                  variant="light"
                  size="sm"
                  onClick={loadEnrolledStudents}
                  disabled={loadingStudents}
                >
                  {loadingStudents ? "Loading..." : "Refresh List"}
                </Button>
              </Card.Header>
              <Card.Body>
                {loadingStudents ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-info" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : students.length === 0 ? (
                  <p className="text-center text-muted py-4">
                    No students enrolled in this course yet. Click "Add Student"
                    to add one.
                  </p>
                ) : (
                  <Table striped hover responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Student Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Enrolled Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student, index) => (
                        <tr key={student.id}>
                          <td>{index + 1}</td>
                          <td>{student.name}</td>
                          <td>{student.email}</td>
                          <td>{student.phone}</td>
                          <td>
                            {new Date(student.enrolled_at).toLocaleDateString()}
                           </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            {/* Quick Actions Card */}
            <Card className="shadow-sm mb-4">
              <Card.Header className="bg-success text-white">
                <h5 className="mb-0">Quick Actions</h5>
              </Card.Header>
              <Card.Body>
                <div className="d-grid gap-2">
                  <Button
                    variant="outline-primary"
                    className="text-start"
                    onClick={() => navigate(`/admin/courses/${id}/materials`)}
                  >
                    <FaBook className="me-2" /> Manage Course Materials
                  </Button>

                  <Button
                    variant="outline-success"
                    className="text-start"
                    onClick={openAddModal}
                  >
                    <FaUserPlus className="me-2" /> Add Student to Course
                  </Button>

                  {course.syllabus_name && (
                    <Button
                      variant="outline-info"
                      className="text-start"
                      onClick={handleDownloadSyllabus}
                    >
                      <FaFilePdf className="me-2" /> Download Syllabus
                    </Button>
                  )}

                  <Button
                    variant="outline-warning"
                    className="text-start"
                    onClick={() => setShowEditModal(true)}
                  >
                    <FaEdit className="me-2" /> Edit Course Details
                  </Button>
                </div>

                <hr />

                <h6 className="mb-3">Course Statistics</h6>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Priority:</span>
                    <strong>{course.priority || 'Not set'}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Status:</span>
                    <Badge bg={course.status === 'active' ? 'success' : 'secondary'}>
                      {course.status || 'active'}
                    </Badge>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Total Modules:</span>
                    <strong>{modules.length}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Has Syllabus:</span>
                    <strong>{course.syllabus_name ? 'Yes' : 'No'}</strong>
                  </div>
                </div>

                <hr />

                {(course.meta_title || course.meta_description) && (
                  <>
                    <h6 className="mb-3">
                      <FaInfoCircle className="me-2" /> SEO Information
                    </h6>
                    {course.meta_title && (
                      <div className="mb-2">
                        <small className="text-muted d-block">Meta Title:</small>
                        <small>{course.meta_title}</small>
                      </div>
                    )}
                    {course.meta_description && (
                      <div>
                        <small className="text-muted d-block">Meta Description:</small>
                        <small>{course.meta_description.substring(0, 100)}...</small>
                      </div>
                    )}
                    <hr />
                  </>
                )}

                <div className="text-muted small">
                  <p className="mb-1">
                    <FaCalendarAlt className="me-2" />
                    <strong>Created:</strong>{" "}
                    {new Date(course.created_at).toLocaleDateString()}
                  </p>
                  <p className="mb-0">
                    <FaCalendarAlt className="me-2" />
                    <strong>Last Updated:</strong>{" "}
                    {new Date(course.updated_at).toLocaleDateString()}
                  </p>
                </div>
              </Card.Body>
            </Card>

            {course.image_url && (
              <Card className="shadow-sm">
                <Card.Header className="bg-secondary text-white">
                  <h5 className="mb-0">
                    <FaImage className="me-2" /> Course Image
                  </h5>
                </Card.Header>
                <Card.Body className="text-center">
                  <img
                    src={course.image_url}
                    alt={course.name}
                    className="img-fluid rounded"
                    style={{ maxHeight: "200px" }}
                  />
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>

        {/* Edit Course Modal */}
        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <FaEdit className="me-2 text-warning" />
              Edit Course
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <CourseForm
              course={course}
              onSuccess={handleEditSuccess}
              onCancel={() => setShowEditModal(false)}
            />
          </Modal.Body>
        </Modal>

        {/* Add Student Modal - UPDATED with Fee Input */}
        <Modal
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <FaUserPlus className="me-2 text-success" />
              Add Student to Course
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* NEW: Course Fee Input - Common for both tabs */}
            <div className="mb-4 p-3 bg-light rounded">
              <Form.Label className="fw-bold">
                <FaMoneyBill className="me-2 text-primary" />
                Course Fee (₹)
              </Form.Label>
              <Form.Control
                type="number"
                value={customFee}
                onChange={(e) => setCustomFee(e.target.value)}
                placeholder="Enter course fee"
                min="0"
                step="100"
              />
              <Form.Text className="text-muted">
                Default course fee is ₹{parseFloat(course?.price || 0).toLocaleString()}. 
                Modify if giving discount or special pricing.
              </Form.Text>
            </div>

            <Nav variant="tabs" className="mb-4">
              <Nav.Item>
                <Nav.Link
                  active={addMethod === "manual"}
                  onClick={() => setAddMethod("manual")}
                >
                  Add New Student
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  active={addMethod === "existing"}
                  onClick={() => {
                    setAddMethod("existing");
                    loadAvailableStudents();
                  }}
                >
                  Select Existing Student
                </Nav.Link>
              </Nav.Item>
            </Nav>

            {addMethod === "manual" ? (
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Full Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter student name"
                    value={manualStudent.name}
                    onChange={(e) =>
                      setManualStudent({
                        ...manualStudent,
                        name: e.target.value,
                      })
                    }
                    isInvalid={!!manualErrors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {manualErrors.name}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Email <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter email"
                    value={manualStudent.email}
                    onChange={(e) =>
                      setManualStudent({
                        ...manualStudent,
                        email: e.target.value,
                      })
                    }
                    isInvalid={!!manualErrors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {manualErrors.email}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Phone Number <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="Enter phone number"
                    value={manualStudent.phone}
                    onChange={(e) =>
                      setManualStudent({
                        ...manualStudent,
                        phone: e.target.value,
                      })
                    }
                    isInvalid={!!manualErrors.phone}
                  />
                  <Form.Control.Feedback type="invalid">
                    {manualErrors.phone}
                  </Form.Control.Feedback>
                </Form.Group>

                <small className="text-muted">
                  <strong>Note:</strong> New student will be registered and automatically approved. 
                  A password will be generated automatically. Course fee: ₹{parseFloat(customFee || 0).toLocaleString()}
                </small>
              </Form>
            ) : (
              <>
                <InputGroup className="mb-3">
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search students by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>

                {loadingAvailable ? (
                  <div className="text-center py-4">
                    <div
                      className="spinner-border text-primary"
                      size="sm"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    <ListGroup>
                      {availableStudents
                        .filter(
                          (s) =>
                            s.name
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase()) ||
                            s.email
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase()),
                        )
                        .map((student) => (
                          <ListGroup.Item
                            key={student.id}
                            className="d-flex justify-content-between align-items-center"
                            action
                          >
                            <div>
                              <strong>{student.name}</strong>
                              <br />
                              <small className="text-muted">
                                {student.email} | {student.phone}
                              </small>
                            </div>
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => handleAddExisting(student.id)}
                            >
                              Add to Course (₹{parseFloat(customFee || 0).toLocaleString()})
                            </Button>
                          </ListGroup.Item>
                        ))}
                      {availableStudents.length === 0 && (
                        <p className="text-center text-muted py-3">
                          No available students found
                        </p>
                      )}
                    </ListGroup>
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              <FaTimes className="me-2" /> Cancel
            </Button>
            {addMethod === "manual" && (
              <Button variant="success" onClick={handleManualAdd}>
                <FaSave className="me-2" /> Add Student (₹{parseFloat(customFee || 0).toLocaleString()})
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default CourseDetails;